import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { store } from './store';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { resumeFromCache } from './store/login/actions';
import * as Bixby from './core';
import './index.scss';

import { LayoutRoute } from './components/global/routing/LayoutRoute';
import { AuthenticatedLayoutRoute } from './components/global/routing/AuthenticatedLayoutRoute';
import MsalAuthenticationRequired from './components/msalAuthenticationRequired';

import LoginLayout from './layouts/LoginLayout';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';
import AuthenticatedAdminLayout from './layouts/AuthenticatedAdminLayout';

import RootComponent from './pages/root';
import LoginContainer from './pages/login/LoginContainer';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password/';
import UserEmailConfirmation from './pages/user-email-confirmation';
import Logout from './pages/logout';

import VerifyCompany from './pages/verify-company';
import ClientPortfolio from './pages/client/portfolio';
import ClientNewsEarnings from './pages/client/news-earnings';
import ClientTransactionComps from './pages/client/transaction-comps';
import ClientAnalytics from './pages/client/analytics';
import CloAnalyticsRoot from './pages/client/clo-analytics';
import CloDetails from './pages/client/clo-analytics/clo-detail';
import CloDealDetails from '$/pages/client/clo-analytics/clo-deal-detail';
import ClientCompany from './pages/client/company';
import DataSitesAccess from './pages/client/data-sites/Access';
import DataSitesRemovals from './pages/client/data-sites/Removals';
import ClientUserGuides from './pages/client/help-center/user-guides';
import ClientVideoTutorials from './pages/client/help-center/video-tutorials';
import ClientExcelPlugin from './pages/client/help-center/excel-plugin';
import ClientCovenantReview from './pages/client/covenant-review';

import Admin from './pages/admin';
import AdminCompanyList from './pages/admin/company/';
import AdminPublicCompanyList from './pages/admin/public-company/';
import AdminCompany from './pages/admin/company/company-detail';
import AdminClientList from './pages/admin/client';
import AdminClient from './pages/admin/client/client-detail';
import AdminCloListRoot from './pages/admin/clo';
import AdminClo from './pages/admin/clo/clo-detail';
import AdminCloDeal from './pages/admin/clo/clo-deal-detail';
import AdminSendCompanyUpdate from './pages/admin/send-company-update';
import AdminUserList from './pages/admin/user/';
import AdminUserDetail from './pages/admin/user/user-detail';
import AdminDataSites from './pages/admin/data-sites';
import AdminReconciliations from './pages/admin/reconciliations';
import AdminPendingApproval from './pages/admin/pending-approval';
import AdminSettings from './pages/admin/settings';
import AdminTranscripts from './pages/admin/transcripts';
import AdminTranscriptUpload from './pages/admin/transcript-upload';
import AdminTranscriptPublish from './pages/admin/transcript-publish';
import AdminCompliance from './pages/admin/compliance';
import AdminHealthPlanner from './pages/admin/health/planner';
import AdminDataSiteDocuments from './pages/admin/data-sites-documents';
import AdminIssuerLevelMapping from './pages/admin/issuer-level-mapping';

import TermsOfService from './pages/terms-of-service';
import Disclosures from './pages/disclosures';
import PrivacyPolicy from './pages/privacy-policy';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getReleaseFeatureFlags } from './store/release-feature-flags/actions';
import CompanyDocumentsHistory from './pages/company-documents-history';
import CompanyDocumentsHistoryDetail from './pages/company-documents-history/detail';
import { Navigate, Route, Router, Routes } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import MyIndustries from './pages/settings/MyIndustries';
import MyCompanies from './pages/settings/MyCompanies';
import NotFound from './pages/not-found';
import Compliance from '$/pages/client/compliance';
import PrivateEquitySponsorsPage from '$/pages/privateEquitySponsors';
import SfdrWhitePaperDownload from '$/components/sfdr/SfdrWhitePaperDownload';
import { CscpIntegrationService } from '$/services/CscpIntegrationService';
import { MsalService } from '$/services';

const App: React.FC = () => {
  const history = createBrowserHistory();
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  /** Only admin links should be exposed for Bixby in Credit Sights */
  const isStandAlone = Bixby.Configuration.isStandAloneMode;
  useLayoutEffect(() => {
    let conditionalWindowListener: (() => void) | null = null;
    const unlistenState = history.listen(setState);
    if (!isStandAlone) {
      // Process the inital page request before we setup msal to account for potential redirect
      if (!MsalService.setUpComplete) {
        CscpIntegrationService.processLocationForMsal(history.location);
      }
      // Update parent window of path changes
      conditionalWindowListener = history.listen(CscpIntegrationService.postMessageHandler);
    }
    return () => {
      unlistenState();
      if (!isStandAlone && conditionalWindowListener) {
        conditionalWindowListener();
      }
    };
  }, [history, isStandAlone]);

  const routes = useMemo(() => {
    return (
      <Routes>
        {/* Redirects */}
        {/* isStandAlone referenced for each link to prep for incremental link exposure to Bixby in CS app version */}
        {isStandAlone && <Route path="/dashboard" element={<Navigate to="/portfolio" replace />} />}
        {isStandAlone && <Route path="/dashboard/news-earnings" element={<Navigate to="/news-earnings" replace />} />}
        {/* General */}
        <Route path="/*" element={<LayoutRoute layout={LoginLayout} component={RootComponent} />} />
        <Route path="/login/:from?" element={<LayoutRoute layout={LoginLayout} component={LoginContainer} />} />
        <Route path="/forgot-password" element={<LayoutRoute layout={LoginLayout} component={ForgotPassword} />} />
        <Route
          path="/resetPassword/:userId?/:code?"
          element={<LayoutRoute layout={LoginLayout} component={ResetPassword} />}
        />
        <Route
          path="/emailConfirmation/:userId?/:code?"
          element={<LayoutRoute layout={LoginLayout} component={UserEmailConfirmation} />}
        />
        <Route path="/logout" element={<LayoutRoute layout={LoginLayout} component={Logout} />} />

        {/* Client */}
        {isStandAlone && (
          <Route
            path="/verifyCompany/:clientId?/:code?"
            element={<AuthenticatedLayoutRoute layout={LoginLayout} component={VerifyCompany} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/portfolio"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientPortfolio} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/myportfolio"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientPortfolio} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/news-earnings"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientNewsEarnings} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/transaction-comps"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientTransactionComps} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/data-sites/access/*"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={DataSitesAccess} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/data-sites/removals"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={DataSitesRemovals} />}
          />
        )}
        {isStandAlone && <Route path="/data-sites" element={<Navigate to="/data-sites/access" replace />} />}
        {isStandAlone && (
          <Route
            path="/analytics"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientAnalytics} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/clo-analytics"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={CloAnalyticsRoot} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/clo-analytics/sfdrwhitepaper"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={SfdrWhitePaperDownload} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/clo-analytics/:cloId/*"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={CloDetails} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/clodeal-analytics/:cloId/*"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={CloDealDetails} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/company/:companyId/*"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientCompany} />}
          />
        )}
        <Route
          path="/help/user-guides"
          element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientUserGuides} />}
        />
        <Route
          path="/help/video-tutorials"
          element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientVideoTutorials} />}
        />
        <Route
          path="/help/excel-plugin"
          element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientExcelPlugin} />}
        />
        <Route path="/help" element={<Navigate to="/help/user-guides" replace />} />
        {isStandAlone && (
          <Route
            path="/settings/my-industries"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={MyIndustries} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/settings/my-companies"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={MyCompanies} />}
          />
        )}
        {isStandAlone && <Route path="/settings" element={<Navigate to="/settings/my-industries" replace />} />}
        {isStandAlone && (
          <Route
            path="/company-documents"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={CompanyDocumentsHistory} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/company-documents/:companyId/*"
            element={
              <AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={CompanyDocumentsHistoryDetail} />
            }
          />
        )}
        {isStandAlone && (
          <Route
            path="/compliance/*"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={Compliance} />}
          />
        )}
        {isStandAlone && (
          <Route
            path="/covenant-review"
            element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={ClientCovenantReview} />}
          />
        )}

        {/* Admin */}
        <Route
          path="/admin"
          element={<AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={Admin} />}
        />
        <Route
          path="/admin/send-company-update/:companyId?"
          element={
            <AuthenticatedLayoutRoute
              onlyAdmin
              layout={AuthenticatedAdminLayout}
              component={props => {
                return (
                  <MsalAuthenticationRequired {...props}>
                    {' '}
                    <AdminSendCompanyUpdate {...props} />{' '}
                  </MsalAuthenticationRequired>
                );
              }}
            />
          }
        />
        <Route
          path="/admin/company/*"
          element={
            <AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminCompanyList} />
          }
        />
        <Route
          path="/admin/public-company/*"
          element={
            <AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminPublicCompanyList} />
          }
        />
        <Route
          path="/admin/company/:companyId/*"
          element={
            <AuthenticatedLayoutRoute
              onlyAdmin
              layout={AuthenticatedAdminLayout}
              component={props => {
                return (
                  <MsalAuthenticationRequired {...props}>
                    {' '}
                    <AdminCompany {...props} />{' '}
                  </MsalAuthenticationRequired>
                );
              }}
            />
          }
        />
        <Route
          path="/admin/transcripts/:transcriptionId/upload"
          element={
            <AuthenticatedLayoutRoute
              onlyAdmin
              layout={AuthenticatedAdminLayout}
              component={props => {
                return (
                  <MsalAuthenticationRequired {...props}>
                    {' '}
                    <AdminTranscriptUpload {...props} />{' '}
                  </MsalAuthenticationRequired>
                );
              }}
            />
          }
        />
        <Route
          path="/admin/send-company-update/:companyId/transcript/:transcriptId"
          element={
            <AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminTranscriptPublish} />
          }
        />
        <Route
          path="/admin/transcripts"
          element={
            <AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminTranscripts} />
          }
        />
        <Route
          path="/admin/data-site-documents"
          element={
            <AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminDataSiteDocuments} />
          }
        />
        <Route
          path="/admin/client"
          element={<AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminClientList} />}
        />
        <Route
          path="/admin/client/:clientId/*"
          element={<AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminClient} />}
        />
        <Route
          path="/admin/user"
          element={<AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminUserList} />}
        />
        <Route
          path="/admin/user/:userId/*"
          element={<AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminUserDetail} />}
        />
        <Route
          path="/admin/data-sites/*"
          element={<AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminDataSites} />}
        />
        <Route
          path="/admin/pending-approval"
          element={
            <AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminPendingApproval} />
          }
        />
        <Route
          path="/admin/reconciliations/*"
          element={
            <AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminReconciliations} />
          }
        />
        <Route
          path="/admin/issuer-level-mapping"
          element={
            <AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminIssuerLevelMapping} />
          }
        />
        <Route
          path="/admin/settings/*"
          element={<AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminSettings} />}
        />
        <Route
          path="/admin/clo/manage/*"
          element={
            <AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminCloListRoot} />
          }
        />
        <Route
          path="/admin/clo/:cloId/*"
          element={
            <AuthenticatedLayoutRoute
              onlyAdmin
              layout={AuthenticatedAdminLayout}
              component={props => {
                return (
                  <MsalAuthenticationRequired {...props}>
                    <AdminClo {...props} />{' '}
                  </MsalAuthenticationRequired>
                );
              }}
            />
          }
        />
        <Route
          path="/admin/clodeal/:cloId/*"
          element={
            <AuthenticatedLayoutRoute
              onlyAdmin
              layout={AuthenticatedAdminLayout}
              component={props => {
                return (
                  <MsalAuthenticationRequired {...props}>
                    <AdminCloDeal {...props} />{' '}
                  </MsalAuthenticationRequired>
                );
              }}
            />
          }
        />
        <Route
          path="admin/private-equity-sponsors"
          element={<AuthenticatedLayoutRoute layout={AuthenticatedAdminLayout} component={PrivateEquitySponsorsPage} />}
        />
        <Route
          path="/admin/compliance/*"
          element={<AuthenticatedLayoutRoute onlyAdmin layout={AuthenticatedAdminLayout} component={AdminCompliance} />}
        />
        <Route
          path="/admin/health/planner"
          element={<AuthenticatedLayoutRoute layout={AuthenticatedAdminLayout} component={AdminHealthPlanner} />}
        />
        <Route path="/admin/health" element={<Navigate to="/admin/health/planner" replace />} />

        <Route
          path="/terms-of-service"
          element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={TermsOfService} />}
        />
        <Route
          path="/disclosures"
          element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={Disclosures} />}
        />
        <Route
          path="/privacy-policy"
          element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={PrivacyPolicy} />}
        />
        <Route path="*" element={<AuthenticatedLayoutRoute layout={AuthenticatedLayout} component={NotFound} />} />
      </Routes>
    );
  }, [isStandAlone]);

  useEffect(() => {
    store.dispatch(resumeFromCache() as any);
    store.dispatch(getReleaseFeatureFlags() as any);
  }, []);
  let basename = '/';
  if (!Bixby.Configuration.isStandAloneMode) {
    basename = Bixby.Configuration?.cscpIframePathPrefix ?? '/';
  }
  return (
    <Provider store={store}>
      <DndProvider backend={HTML5Backend}>
        <Router location={state.location} navigationType={state.action} navigator={history} basename={basename}>
          {routes}
        </Router>
        <ToastContainer />
      </DndProvider>
    </Provider>
  );
};

export default App;
